import type { Option } from "@tento-lona/sheets";
import "@tento-ui/components/round";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { Tracing } from "@tento-core/tracing";
import { Round } from "@tento-ui/components/round";
import { type SheetRow } from "@tento-lona";
import { SheetViewModel } from "@tento-lona";
import type { SheetBinder } from "./sheet-index-renderer.ts";

@component({
  name: "std-row-options-anchor",
})
export class RowOptionsAnchor extends LonaWebComponent {
  $popover: Round = this.hydrate<Round>("root").setAnimates(true);
  $content: Option<HTMLElement> = null;
  #onDismissCallback: Option<() => void> = null;

  state: {
    contentHeight?: number;
    scrollTop?: number;
    scrollHeight?: number;
    viewportHeight?: number;
  } = {};

  #ro = new ResizeObserver(([entry]) => {
    if (entry === undefined) return;
    this.state.contentHeight = entry.contentRect.height;
    this.#updateTransform();
  });

  connectedCallback() {
    super.connectedCallback();
    if (this.$content) {
      this.#ro.observe(this.$content);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ro.disconnect();
    GESTURE_MANAGER.clearOnClickOutside(this.$popover);
  }

  dismiss() {
    this.#onDismissCallback?.();
    DomUtils.clearChildren(this.$popover);
    GESTURE_MANAGER.clearOnClickOutside(this.$popover);
  }

  didViewportScroll(top: number, scrollHeight: number, viewportHeight: number) {
    this.state.scrollHeight = scrollHeight;
    this.state.scrollTop = top;
    this.state.viewportHeight = viewportHeight;
    this.#updateTransform();
  }

  #onSpaceBottomChanged: Option<
    (scrollHeight: number, contentHeight: number) => void
  > = null;

  #updateTransform() {
    if (!this.state) return;
    const { contentHeight, scrollTop, scrollHeight, viewportHeight } =
      this.state;
    if (
      contentHeight == null ||
      scrollTop == null ||
      scrollHeight == null ||
      viewportHeight == null
    )
      return;
    this.#onSpaceBottomChanged?.(scrollHeight, contentHeight);
  }

  #isContextualTarget($target: Option<HTMLElement>): boolean {
    if ($target == null) return false;
    for (const $e of [$target, ...DomUtils.getAncestors($target)]) {
      const context = $e.getAttribute("context");
      if (context != null && context !== "") return true;
    }
    return false;
  }

  bind(
    $content: HTMLElement,
    sheet: SheetViewModel.Surface<SheetBinder>,
    row: SheetRow.Joined.Handle,
    legendWidthPx: number,
    onDismiss: () => void,
  ) {
    this.$content = $content;
    this.#ro.disconnect();
    this.#ro.observe(this.$content);
    const isFrozen = sheet.joinedTree.isInVisibleHeader(row.id);

    Tracing.dev("row-options/content", this.$content);

    let top = 0;
    {
      let layout = sheet.layout.rowLayout.get(row.id);
      if (layout == null) {
        // Transparent-alias rows (e.g. weather / garmin health sources) are
        // inlined away during flattening, so they own no layout entry — the
        // inlined source child renders in the alias's place. Anchor to that
        // child's layout so the popover positions and sizes correctly instead
        // of collapsing to top:0 / height:"undefinedpx" (a no-op open).
        for (const child of row.children()) {
          const childLayout = sheet.layout.rowLayout.get(child.id);
          if (childLayout != null) {
            layout = childLayout;
            break;
          }
        }
      }
      const frozenHeight = sheet.layout.headerHeightPx;
      if (isFrozen) {
        this.slot = "header-legend";
        this.style.left = legendWidthPx + "px";
      } else {
        this.slot = "legend";
        this.style.left = "100%";
      }
      this.toggleAttribute("use-body-margin", !isFrozen);
      if (isFrozen) {
        top += layout?.offsetPx ?? 0;
        this.style.top = top + "px";
      } else {
        top += (layout?.offsetPx ?? 0) - frozenHeight + 12;
        this.style.top = top + "px";
      }
      this.style.height = `${layout?.cellHeightWithChildrenPx ?? 0}px`;
      this.style.overflow = "visible";
    }
    this.#onSpaceBottomChanged = (reportedScrollHeight, contentHeight) => {
      const footerHeight = sheet.layout.footerRowsHeightPx;
      const scrollHeight = Math.max(
        sheet.layout.totalHeightPx,
        window.innerHeight,
      );
      // `remaining` is the content-space distance from the anchor to the
      // body bottom (excluding the sticky footer). It uses `totalHeightPx`
      // (the logical sum of all row heights), but the actual DOM content is
      // ~20px taller due to inline-table sizing on #content, header/footer
      // border padding, and min-height clamping. The -20 lets the popover
      // use that extra space so it doesn't shift up more than needed.
      const remaining = scrollHeight - (top + sheet.layout.headerHeightPx) - footerHeight;
      const layoutSlack = 20;
      const overshoot = remaining - contentHeight + layoutSlack;

      if (!isFrozen && overshoot < 0) {
        this.$popover.style.transform = `translateY(${overshoot}px)`;
      } else {
        this.$popover.style.transform = "";
      }
    };

    const frozenHeight = sheet.layout.headerHeightPx + 2;
    this.$popover.assignStyles({
      display: "block",
      position: "sticky",
      top: isFrozen ? "0px" : frozenHeight + 12 + "px",
      width: "320px",
      cursor: "auto",
    });
    this.$popover.toggleAttribute("frozen", isFrozen);
    DomUtils.clearChildren(this.$popover);
    this.$popover.appendChild($content);
    this.$popover.onpointerup = (e) => e.stopPropagation();
    this.#onDismissCallback = onDismiss;
    GESTURE_MANAGER.setOnClickOutside(this.$popover, ($e) => {
      if ($e === this.$popover) return;
      if (this.#isContextualTarget($e ?? null)) return;
      if (
        $e &&
        ($e.hasAttribute("emoji-target") || $e.hasAttribute("label-target"))
      ) {
        for (const $a of DomUtils.getAncestors($e)) {
          const rid = $a?.getAttribute("row-id");
          if (rid != null) {
            if (rid === row.id.toString()) return;
            break;
          }
        }
      }

      this.#onDismissCallback?.();
      DomUtils.clearChildren(this.$popover);
      GESTURE_MANAGER.clearOnClickOutside(this.$popover);
    });
  }

  static $styles: Style.Compat[] = [
    css`
      :host {
        display: block;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-round id="root" bg="var(--bg)" continuous="0.6" radius="20" shadow="0 1px 2px rgba(0,0,0,0.4)"></std-round>
  `;
}
